import { useState } from 'react';
import {
  useCreateSignupInvite,
  useMe,
  useRevokeSignupInvite,
  useSignupInvites,
} from '../api/hooks.js';
import { formatDate } from '../lib/format.js';
import { ApiError } from '../api/client.js';
import SignupCodesCard from '../components/SignupCodesCard.js';

export default function UserSettings() {
  const me = useMe();
  const signupInvites = useSignupInvites();
  const create = useCreateSignupInvite();
  const revoke = useRevokeSignupInvite();

  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  if (!me.data) return null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Settings</h1>
        <p className="mt-1 text-sm text-slate-500">Your account profile and platform invites.</p>
      </div>

      <section className="card p-5">
        <h2 className="text-sm font-semibold text-slate-700">Profile</h2>
        <dl className="mt-3 grid grid-cols-2 gap-y-3 text-sm">
          <dt className="text-slate-500">Name</dt>
          <dd className="text-slate-900">{me.data.user.name ?? '—'}</dd>
          <dt className="text-slate-500">Email</dt>
          <dd className="text-slate-900">{me.data.user.email}</dd>
          <dt className="text-slate-500">Joined</dt>
          <dd className="text-slate-900">{formatDate(me.data.user.created_at)}</dd>
        </dl>
      </section>

      <section className="card p-5">
        <h2 className="text-sm font-semibold text-slate-700">Invite someone to adhc.to</h2>
        <p className="mt-1 text-sm text-slate-600">
          Send a sign-up invite. They'll create their own account and workspace.
          Different from workspace invites (which add someone to a workspace you already own).
        </p>
        <form
          className="mt-4 flex flex-wrap items-end gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            setSuccess(null);
            try {
              await create.mutateAsync({ email });
              setSuccess(`Invite sent to ${email}.`);
              setEmail('');
            } catch (err) {
              if (err instanceof ApiError) {
                const code = (err.payload as { error?: string })?.error ?? 'request_failed';
                setError(prettyError(code));
              } else {
                setError('Something went wrong.');
              }
            }
          }}
        >
          <div className="flex-1 min-w-[200px]">
            <label className="label">Email</label>
            <input
              type="email"
              required
              className="input mt-1"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <button type="submit" disabled={create.isPending} className="btn-primary">
            {create.isPending ? 'Sending…' : 'Send invite'}
          </button>
        </form>
        {error && (
          <p className="mt-2 rounded-md bg-red-50 p-2 text-sm text-red-700">{error}</p>
        )}
        {success && (
          <p className="mt-2 rounded-md bg-emerald-50 p-2 text-sm text-emerald-700">{success}</p>
        )}
      </section>

      <SignupCodesCard />

      <section className="card p-5">
        <h2 className="text-sm font-semibold text-slate-700">Invites you've sent</h2>
        <ul className="mt-3 divide-y divide-slate-100">
          {(signupInvites.data ?? []).length === 0 ? (
            <li className="py-3 text-sm text-slate-500">No sign-up invites yet.</li>
          ) : (
            (signupInvites.data ?? []).map((i) => (
              <li key={i.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-slate-900">{i.email}</p>
                  <p className="text-xs text-slate-500">
                    {i.accepted_at
                      ? `accepted ${formatDate(i.accepted_at)}`
                      : `expires ${formatDate(i.expires_at)}`}
                  </p>
                </div>
                {!i.accepted_at && (
                  <button
                    className="text-xs text-red-600 hover:underline"
                    onClick={() => revoke.mutate(i.id)}
                  >
                    Revoke
                  </button>
                )}
              </li>
            ))
          )}
        </ul>
      </section>
    </div>
  );
}

function prettyError(code: string): string {
  switch (code) {
    case 'account_already_exists':
      return 'That address already has an account.';
    default:
      return code.replace(/_/g, ' ');
  }
}
